<template>
    <div :class="['flex h-24 w-full items-center bg-button-panel-background px-0 py-2.5 shadow-button-panel-box-shadow xl:p-0', props.finishState ? 'justify-center' : 'justify-between']">
        <slot name="leftside">
            <Button
                v-if="!props.finishState"
                class="btn-cancel"
                type="button"
                @click="onCancel"
                :title="$t('go-back-title')"
                :label="$t('go-back-title')"
                :pt-options="{
                    mergeProps: true,
                    mergeSections: true,
                }"
                :pt="{
                    root: () => ({
                        class: [
                            `w-52 text-sm ml-5 mr-5 xl:ml-12 !rounded-md border border-button-panel-cancel-border-c bg-button-panel-cancel-bg-c hover:!border-button-panel-cancel-border-hover-c 
                         hover:!bg-button-panel-cancel-bg-hover-c`,
                            props.showCancel === true ? '' : '!invisible',
                        ],
                    }),
                    label: { class: 'text-button-panel-cancel-text-c !font-normal' },
                }"
            >
            </Button>
        </slot>
        <div v-if="!props.finishState && props.hintComplete !== true" :class="['flex items-center', !props.hintText ? 'invisible' : '']">
            <div
                v-if="!props.hintTextIsError"
                v-tooltip.focus.top="{
                    value: props.hintText,
                    disabled: !isMobile,
                }"
                tabindex="1"
            >
                <FontAwesomeLayers class="fa-2x !pt-10 text-button-panel-hint-c">
                    <FontAwesomeIcon icon="fa-sharp fa-thin fa-circle" />
                    <FontAwesomeIcon icon="fa-sharp fa-solid fa-question" transform="shrink-7" />
                </FontAwesomeLayers>
            </div>
            <p :class="['ml-2.5 hidden text-sm text-button-panel-hint-c xl:block', props.hintTextIsError ? 'text-red-500' : 'text-button-panel-hint-c']">{{ props.hintText }}</p>
        </div>
        <div class="invisible w-32" v-if="!showSubmit"></div>
        <Button
            v-if="showSubmit"
            type="button"
            @click="onSubmit"
            :disabled="props.submitDisabled || props.loading"
            :label="props.submitTitle"
            :loading="props.loading"
            :title="props.submitTitle"
            :size="props.submitSize"
            :pt-options="{
                mergeProps: true,
                mergeSections: true,
            }"
            :pt="{
                root: () => ({
                    class: [
                        'w-52 ml-5 text-sm border-none rounded-md !py-4',
                        props.finishState ? ' mr-0' : 'mr-5 xl:mr-12',
                        props.errorBtn ? 'bg-danger' : 'bg-button-panel-select-bg-c',
                        props.showSubmit === true ? '' : 'invisible',
                        props.submitDisabled ? 'hover:!bg-button-panel-select-bg-c' : 'hover:opacity-40',
                    ],
                }),
                label: { class: '!text-button-panel-select-text-c !font-normal' },
            }"
        >
        </Button>
    </div>
</template>

<script setup lang="ts">
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core/index';
import { PropType } from 'vue';

const props = defineProps({
    finishState: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    showCancel: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    showSubmit: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: true,
    },
    submitDisabled: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    loading: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    submitTitle: {
        type: String as PropType<string>,
        required: false,
        default: '',
    },
    errorBtn: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    hintText: {
        type: String as PropType<string>,
        required: false,
        default: '',
    },
    hintTextIsError: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    hintComplete: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
    },
    submitSize: {
        type: String as PropType<'small' | 'large'>,
        required: false,
        default: null,
    },
});

const emits = defineEmits(['cancel', 'submit']);

const breakpoints = useBreakpoints(breakpointsTailwind);

const isMobile = breakpoints.smaller('xl');

const onCancel = () => {
    emits('cancel');
};

const onSubmit = () => {
    if (props.submitDisabled) {
        return;
    }

    emits('submit');
};
</script>
